class ImpactText extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.animationFrame = null;
    this.isRevealed = false;
    this.handleIntersection = this.handleIntersection.bind(this);
  }

  connectedCallback() {
    this.style.display = 'inline-block';
    this.style.opacity = '0';
    this.style.transform = 'translateY(10px)';

    if (this.shouldReduceMotion()) {
      this.reveal();
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '-100px'
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  handleIntersection(entries) {
    if (!entries.some((entry) => entry.isIntersecting) || this.isRevealed) {
      return;
    }

    this.observer?.disconnect();
    this.observer = null;
    this.reveal();
  }

  shouldReduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  reveal() {
    if (this.isRevealed) {
      return;
    }

    this.isRevealed = true;
    this.style.transition = 'opacity 300ms ease, transform 300ms ease';

    requestAnimationFrame(() => {
      this.style.opacity = '1';
      this.style.transform = 'translateY(0)';
    });

    const duration = Number.parseFloat(this.getAttribute('count-up') || '0');
    if (duration > 0 && !this.shouldReduceMotion()) {
      this.animateNumber(duration * 1000);
    }
  }

  animateNumber(duration) {
    const textContainer = this.firstElementChild || this;
    const originalText = textContainer.textContent || '';
    const numberMatch = originalText.match(/\d+(?:[,. ]\d+)*/);

    if (!numberMatch) {
      return;
    }

    const originalNumber = numberMatch[0];
    const targetNumber = Number.parseInt(originalNumber.replace(/[,\. ]+/g, ''), 10);
    if (!Number.isFinite(targetNumber)) {
      return;
    }

    const prefix = originalText.slice(0, numberMatch.index);
    const suffix = originalText.slice(numberMatch.index + originalNumber.length);
    const separators = [...originalNumber.matchAll(/[,\. ]+/g)];
    const startTime = performance.now();

    const update = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      let formattedNumber = Math.round(progress * targetNumber).toString();

      separators.forEach((separator) => {
        if (formattedNumber.length >= originalNumber.length - separator.index) {
          formattedNumber = `${formattedNumber.slice(0, separator.index)}${separator[0]}${formattedNumber.slice(separator.index)}`;
        }
      });

      textContainer.textContent = `${prefix}${progress === 1 ? originalNumber : formattedNumber}${suffix}`;

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(update);
      } else {
        this.animationFrame = null;
      }
    };

    this.animationFrame = requestAnimationFrame(update);
  }
}

if (!customElements.get('impact-text')) {
  customElements.define('impact-text', ImpactText);
}
